import * as React from 'react';
import {
    SimpleForm,
    TextInput,
    required,
    SelectInput,
    DateTimeInput,
    FormDataConsumer,
    BooleanInput,
    ArrayInput,
    SelectArrayInput,
    useNotify,
    useRedirect,
} from 'react-admin';
import { useParams } from 'react-router';
import { Box, Tab, Tabs } from '@mui/material';

import SlugInput from '../../common/inputs/SlugInput';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import { EditToolbar } from '../../common/EditToolbar';
import { LoadingSection } from '../../common/LoadingSection';
import { BASE_URL, BASE_URL_GO, STATUS, TEST_CATEGORIES, TEST_TYPES } from '../../common/constants';
import { TabPanel, transformTestData } from './TestCreate';
import { Tests } from '../../custom';
import FileUpload from '../../common/inputs/FileUpload';
import { getLanguages } from '../../services/testSeries-service';
import { restDataProvider } from '../../dataProviders/restDataProvider/restDataProvider';
import { TestSeriesAutocomplete } from '../../common/inputs/autoCompleteInputs/TestSeriesAutoCompleteInput';
import VideoAutocompleteInput from '../../common/inputs/VideoAutocompleteInput';
import { goApi } from '../../services';

const LanguageFields = ({ language }: { language: string }) => {
    return (
        <>
            <HTMLEditorInput
                source={`${language} generalInstructions`}
                fullWidth
                validate={[required(), validHtml]}
            />
            <HTMLEditorInput
                source={`${language} testInstructions`}
                fullWidth
                validate={[required(), validHtml]}
            />
        </>
    );
};

const TestEdit = () => {
    const { testSeriesId, testId } = useParams();
    const notify = useNotify();
    const redirect = useRedirect();
    const [languagesList, setLanguagesList] = React.useState<any[]>([]);
    const [testDetails, setTestDetails] = React.useState<any>();
    const [transformedTestDetails, setTransformedTestDetails] = React.useState<any>();
    const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>([]);
    const [tabIndex, setTabIndex] = React.useState(0);

    const handleChange = (_: any, newValue: number) => setTabIndex(newValue);

    const transformTestDetailsForForm = (testData: any) => {
        if (!testData) return null;

        const transformed = { ...testData };

        if (testData.testLanguages && Array.isArray(testData.testLanguages)) {
            testData.testLanguages.forEach((langData: any) => {
                const langCode = langData.language;

                transformed[`${langCode} generalInstructions`] = langData.generalInstructions;
                transformed[`${langCode} testInstructions`] = langData.testInstructions;
            });
        }
        return transformed;
    };

    const fetchTestDetails = async () => {
        if (!testId) return;

        try {
            const res = await goApi.get(`/tests/${testId}`);
            const test = res?.data;

            setTestDetails(test);
            setSelectedLanguages(test?.languages || []);
        } catch (err: any) {
            console.error('Failed to load test details', err);

            notify(
                `Error fetching test details: ${err?.message || 'Something went wrong'}`,
                { type: 'warning' }
            );
        }
    };

    React.useEffect(() => {
        if (testId) {
            fetchTestDetails();
        }

        (async () => {
            const data = await getLanguages();

            const formatted = Object.entries(data).map(([fullName, code]) => ({
                id: code,
                name: fullName,
            }));

            setLanguagesList(formatted);
        })();
    }, [testId]);

    React.useEffect(() => {
        if (testDetails) {
            const transformed = transformTestDetailsForForm(testDetails);
            setTransformedTestDetails(transformed);
        }
    }, [testDetails]);

    if (!transformedTestDetails) return <LoadingSection />;

    const modifiedPostSave = async (data: any) => {
        try {
            const updateData = transformTestData(data, selectedLanguages);

            await restDataProvider.update('tests', {
                id: testId,
                data: updateData,
            });

            notify('Test updated successfully', { type: 'success' });
            redirect(`/test-series/${testSeriesId}/tests`);
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'warning' });
        }
    };

    return (
        <Box sx={{ width: '100%' }} className="border m-2 p-3 rounded-lg ">
            <SimpleForm
                onSubmit={modifiedPostSave}
                defaultValues={transformedTestDetails}
                toolbar={<EditToolbar />}
            >
                <FileUpload
                    type="VIDEO_THUMBNAIL"
                    label="select image"
                    source="image"
                    accept="image/*"
                    multiple={false}
                />
                <TestSeriesAutocomplete disabled />
                <TextInput source="name" fullWidth validate={[required()]} />
                <SlugInput source="slug" fullWidth />
                <TextInput source="displayOrder" type="number" fullWidth validate={[required()]} />

                <SelectInput
                    source="type"
                    label="Type"
                    fullWidth
                    validate={[required()]}
                    choices={TEST_TYPES}
                />

                <TextInput source="tag" fullWidth />

                <DateTimeInput source={'resultGenerationTime'} fullWidth />

                <BooleanInput source={'reattemptAllowed'} defaultValue={true} />
                <BooleanInput source={'isDemo'} defaultValue={false} />

                <SelectInput
                    source="pattern"
                    label="Category"
                    fullWidth
                    validate={[required()]}
                    choices={TEST_CATEGORIES}
                />

                <SelectInput
                    source="status"
                    label="Status"
                    fullWidth
                    validate={[required()]}
                    choices={STATUS}
                />

                <FormDataConsumer>
                    {({ formData }) =>
                        formData.pattern === 'SUBJECTIVE' ? (
                            <FileUpload
                                type="PDF_NOTES"
                                source="questionFile"
                                label="select question files"
                                accept="application/pdf"
                                multiple={false}
                            />
                        ) : null
                    }
                </FormDataConsumer>

                <DateTimeInput source="startTime" fullWidth validate={[required()]} />
                <DateTimeInput source="endTime" fullWidth validate={[required()]} />

                <TextInput
                    source="maxDurationInMinutes"
                    type="number"
                    fullWidth
                    validate={[required()]}
                />

                <DateTimeInput source="validTill" label="Test Valid Till" fullWidth />

                <FileUpload
                    type="PDF_NOTES"
                    source="solutionFiles"
                    label="select solution files"
                    accept="application/pdf"
                    multiple={true}
                />
                <VideoAutocompleteInput
                    source="solutionVideoId"
                    formData={transformedTestDetails}
                />
                <ArrayInput source="languages">
                    <SelectArrayInput
                        label="Test Languages"
                        choices={languagesList}
                        fullWidth
                        onChange={(e: any) => setSelectedLanguages(e.target.value)}
                        validate={[required()]}
                    />
                </ArrayInput>

                <Tabs value={tabIndex} onChange={handleChange}>
                    {selectedLanguages.map(lang => (
                        <Tab
                            key={lang}
                            label={languagesList.find(l => l.id === lang)?.name || lang}
                        />
                    ))}
                </Tabs>

                {selectedLanguages.map((lang, index) => (
                    <TabPanel key={lang} value={tabIndex} index={index}>
                        <LanguageFields language={lang} />
                    </TabPanel>
                ))}
            </SimpleForm>
        </Box>
    );
};

export default TestEdit;
